import React from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { AnimatePresence, motion } from "framer-motion";

const PlanDetails = ({ plan, billingCycle, discountRate = 0, price, isProPlan = false, colorsMap }) => {
  const planNameLower = plan.name.toLowerCase();

  return (
    <MDBox
      textAlign="center"
      sx={{
        position: "sticky",
        top: 0,
        backgroundColor: colorsMap[planNameLower] || "#f5f5f5",
        pt: 0,
        overflow: "visible",
        border: isProPlan ? "2px solid #00A1A1" : "none",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        minHeight: "80px",
      }}
    >
      {/* Best Value Badge */}
      {isProPlan ? (
        <MDBox mt={0} sx={{ position: "relative", zIndex: 10 }}>
          <MDTypography
            variant="caption"
            fontWeight="bold"
            color="white"
            sx={{
              backgroundColor: "#00A1A1",
              padding: "4px 8px",
              borderRadius: "4px",
              display: "inline-block",
              mt: 1,
            }}
          >
            Best Value
          </MDTypography>
        </MDBox>
      ) : (
        <MDBox sx={{ height: "35px" }} />
      )}

      {/* Plan Name */}
      <MDBox
        sx={{
          zIndex: 2,
          pt: 0.5,
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#000000" }}>
          {plan.name.replace(/plan/i, "").trim()}
        </MDTypography>

        {/* Discount Label */}
        {billingCycle === "Annual" && discountRate > 0 && (
          <MDBox sx={{ position: "relative", top: "3px", ml: 0.5 }}>
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.5 }}
              >
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  color="white"
                  sx={{
                    backgroundColor: "#00A1A1",
                    padding: "1px 8px",
                    borderRadius: "4px",
                    display: "inline-block",
                    mb: 1,
                  }}
                >
                  -{discountRate * 100}%
                </MDTypography>
              </motion.div>
            </AnimatePresence>
          </MDBox>
        )}
      </MDBox>

      {/* Monthly Price */}
      <MDBox
        textAlign="center"
        sx={{
          zIndex: 2,
          pt: 0.5,
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <MDTypography
          variant="h5"
          fontWeight="bold"
          sx={{ color: "#000000", alignSelf: "flex-start" }}
        >
          £
        </MDTypography>
        <AnimatePresence mode="wait">
          <motion.div
            key={`billing-${billingCycle}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <MDTypography variant="h3" fontWeight="bold" sx={{ color: "#000000" }}>
              {price.toFixed(2)}
            </MDTypography>
          </motion.div>
        </AnimatePresence>

        <MDBox sx={{ position: "relative", top: "5px", ml: 0.5 }}>
          <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#000000" }}>
            /month
          </MDTypography>
        </MDBox>
      </MDBox>

      {/* Annual Price */}
      <MDBox
        textAlign="center"
        sx={{
          zIndex: 2,
          pt: 0.5,
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <MDTypography
          variant="subtitle2"
          fontWeight="regular"
          sx={{ fontSize: "0.75rem", alignSelf: "flex-start" }}
        >
          £
        </MDTypography>
        <AnimatePresence mode="wait">
          <motion.div
            key={`billing-annual-${billingCycle}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <MDTypography variant="subtitle2" fontWeight="regular">
              {(price.toFixed(2) * 12).toFixed(2)}
            </MDTypography>
          </motion.div>
        </AnimatePresence>

        <MDBox sx={{ position: "relative", top: "1px", ml: 0.5 }}>
          <MDTypography variant="caption" fontWeight="regular">
            /year
          </MDTypography>
        </MDBox>
      </MDBox>
    </MDBox>
  );
};

PlanDetails.propTypes = {
  plan: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }).isRequired,
  billingCycle: PropTypes.string.isRequired,
  discountRate: PropTypes.number,
  price: PropTypes.number.isRequired,
  isProPlan: PropTypes.bool,
  colorsMap: PropTypes.object.isRequired,
};


export default PlanDetails;
